import React, { PropsWithChildren, ReactNode, JSX, MouseEvent, useRef } from 'react';
import {
  DropdownMenu,
  Dropdown,
  DropdownItem,
  DropdownItemProps,
  DropdownToggle
} from 'reactstrap';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useClickAway } from 'react-use';

export interface Props {
  isOpen: boolean;
  onDropdownToggle: Function;
  title?: ReactNode;
  caret?: boolean;
  icon?: IconDefinition;
  id?: string;
  right?: boolean;
  tabIndex?: number | null;
  disabled?: boolean;
  onToggleButtonClick?: () => void;
  closeCustomDropdownToggle?: () => void;
}

export const IsoDropdownItem = (props: PropsWithChildren<DropdownItemProps>): JSX.Element => {
  const { children, className } = props;
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <DropdownItem {...props} className={`dropdown-item ${className || ''}`}>
      {children}
    </DropdownItem>
  );
};

const IsoDropdown = (props: PropsWithChildren<Props>): JSX.Element => {
  const {
    id,
    children,
    icon,
    caret,
    title,
    onDropdownToggle,
    isOpen,
    right,
    disabled,
    onToggleButtonClick,
    closeCustomDropdownToggle
  } = props;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tabIndex: number | undefined =
    props.tabIndex === null ? undefined : props.tabIndex === undefined ? 1 : props.tabIndex;

  useClickAway(dropdownRef, () => {
    if (closeCustomDropdownToggle) {
      closeCustomDropdownToggle();
    }
  });

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleButtonClick) {
      onToggleButtonClick();
    }
    onDropdownToggle(!isOpen);
  };

  const renderButton = () => {
    return (
      <div className="d-flex">
        {icon && (
          <div className="ml-auto">
            <FontAwesomeIcon icon={icon} />
          </div>
        )}
        <div className="w-100 text-truncate">{title || ''}</div>
      </div>
    );
  };

  const renderMenu = () => {
    return (
      <>
        {isOpen && (
          <DropdownMenu flip={false} right={right} persist={false}>
            {children}
          </DropdownMenu>
        )}
      </>
    );
  };

  return (
    <Dropdown
      id={id}
      className="iso__dropdown"
      isOpen={isOpen}
      toggle={(): void => onDropdownToggle(!isOpen)}
      tabIndex={tabIndex}
      disabled={disabled}
    >
      {closeCustomDropdownToggle && (
        <div ref={dropdownRef}>
          <button
            type="button"
            tabIndex={tabIndex}
            aria-haspopup="true"
            aria-expanded={isOpen}
            onClick={onClick}
            className={classNames('iso__dropdown__toggle btn btn-white', {
              'dropdown-toggle': caret
            })}
          >
            {renderButton()}
          </button>
          {renderMenu()}
        </div>
      )}
      {!closeCustomDropdownToggle && (
        <>
          <DropdownToggle
            color="white"
            className="iso__dropdown__toggle"
            caret={caret}
            onClick={onToggleButtonClick}
            tabIndex={tabIndex}
            disabled={disabled}
          >
            {renderButton()}
          </DropdownToggle>
          {renderMenu()}
        </>
      )}
    </Dropdown>
  );
};
export default IsoDropdown;
